<template>
  <div class="container-fluid">
    <div class="row" id="app">
      <!-- Inputs Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <TopbarNav :pageTitle="'Lag Screw Withdrawal Resistance'" :showHome="true" />
        <hr/>
        
        <!-- Section 1: Basic Parameters -->
        <div class="row">
          <div class="col-6">
            <label for="phi">Resistance factor, φ:</label>
          </div>
          <div class="col-6">
            <input type="number" name="phi" v-model="phi" step="0.01" />
          </div>
        </div>
        
        <hr/>
        
        <!-- Section 2: Specified Withdrawal Resistance -->
        <h5>Specified Withdrawal Resistance (γ<sub>w</sub>)</h5>
        
        <div class="row">
          <div class="col-6">
            <label for="species">Species:</label>
          </div>
          <div class="col-6">
            <select name="species" v-model="species">
              <option value="D Fir-Larch">D Fir-Larch</option>
              <option value="Hem-Fir">Hem-Fir</option>
              <option value="Spruce-Pine-Fir">Spruce-Pine-Fir</option>
              <option value="Northern Species">Northern Species</option>
            </select>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <label for="dF">Shank diameter:</label>
          </div>
          <div class="col-6">
            <select name="dF" v-model="dF">
              <option value="6.35">1/4" (6.35 mm)</option>
              <option value="7.94">5/16" (7.94 mm)</option>
              <option value="9.53">3/8" (9.53 mm)</option>
              <option value="11.11">7/16" (11.11 mm)</option>
              <option value="12.70">1/2" (12.70 mm)</option>
              <option value="15.88">5/8" (15.88 mm)</option>
              <option value="19.05">3/4" (19.05 mm)</option>
              <option value="22.23">7/8" (22.23 mm)</option>
              <option value="25.40">1" (25.40 mm)</option>
            </select>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <label for="materialType">Material type:</label>
          </div>
          <div class="col-6">
            <select name="materialType" v-model="materialType">
              <option value="standard">Standard lumber/timber</option>
              <option value="clt">CLT</option>
            </select>
          </div>
        </div>
        
        <hr/>
        
        <!-- Section 3: Modification Factors -->
        <h5>Modification Factors</h5>
        
        <div class="row">
          <div class="col-6">
            <label for="loadDuration">Load duration:</label>
          </div>
          <div class="col-6">
            <select name="loadDuration" v-model="loadDuration">
              <option value="short">Short term (1.15)</option>
              <option value="standard">Standard term (1.00)</option>
              <option value="long">Long term (0.65)</option>
            </select>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <p class="fst-italic comment">[Table 5.1]</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <label for="moistureContent">Moisture content when fabricated:</label>
          </div>
          <div class="col-6">
            <select name="moistureContent" v-model="moistureContent">
              <option value="dry">Dry (≤ 19%)</option>
              <option value="green">Green (> 19%)</option>
            </select>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <label for="serviceCondition">Service condition:</label>
          </div>
          <div class="col-6">
            <select name="serviceCondition" v-model="serviceCondition">
              <option value="dry">Dry</option>
              <option value="wet">Wet</option>
            </select>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <p class="fst-italic comment">[Table 12.1]</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <label for="KT">Treatment factor, K<sub>T</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="KT" v-model="KT" step="0.01" />
          </div>
        </div>
        <div class="row" v-if="KT == 1">
          <div class="col-12">
            <p class="fst-italic comment">[Clause 5.3.4.3] Untreated wood</p>
          </div>
        </div>
        <div class="row" v-if="KT != 1">
          <div class="col-12">
            <div class="alert alert-warning">
              <p>For wood impregnated with fire-retardant or other strength-reducing chemicals, K<sub>T</sub> shall be determined in accordance with results of appropriate tests or shall not exceed the value specified in the appropriate clause of the Standard.</p>
              <p class="fst-italic">[Clause 5.3.4.4] and [Clause 12.2.1.7.3]</p>
            </div>
          </div>
        </div>
        
        <hr/>
        
        <!-- Section 4: Screw Dimensions -->
        <h5>Screw Dimensions</h5>
        
        <div class="row">
          <div class="col-6">
            <label for="Lt">Length of penetration, L<sub>t</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="Lt" v-model="Lt" step="0.1" /><span>mm</span>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <label for="nF">Number of fasteners, n<sub>F</sub>:</label>
          </div>
          <div class="col-6">
            <input type="number" name="nF" v-model="nF" step="1" />
          </div>
        </div>
        
        <hr/>
        
        <!-- Section 5: End Grain Factor -->
        <h5>End Grain Factor (J<sub>E</sub>)</h5>
        
        <div class="row">
          <div class="col-6">
            <label for="endGrainCondition">Installation condition:</label>
          </div>
          <div class="col-6">
            <select name="endGrainCondition" v-model="endGrainCondition">
              <option value="other">All other cases (1.00)</option>
              <option value="endgrain">End grain (0.75)</option>
              <option value="clt_edge">Panel edge of CLT (0.67)</option>
            </select>
          </div>
        </div>
        
        <div class="row" v-if="endGrainCondition === 'endgrain'">
          <div class="col-12">
            <p class="fst-italic comment">Note: The use of lag screws in end grain should be avoided.</p>
          </div>
        </div>
        
      </div>
      
      <!-- Report Section -->
      <div class="col-12 col-lg-6 engineering-paper">
        <h4>Factored Withdrawal Resistance</h4>
        <p class="fst-italic">CSA O86:19</p>
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'P_{rw} = \\phi Y_w L_t n_F J_E'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">[Clause 12.6.6.1]</p>
          </div>
        </div>
        
        <hr/>
        
        <!-- Calculations -->
        <h5>Calculations:</h5>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\phi = ' + phi" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Resistance factor [Clause 12.6.6.1]</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'Y_w = \\gamma_w(K_D K_{SF} K_T)'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">[Clause 12.6.6.1]</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\gamma_w = 59d_F^{0.82}G^{1.77}J_x'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">[Clause 12.6.6.1]</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'d_F = ' + parseFloat(dF).toFixed(2) + '\\text{ mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Shank diameter of lag screw</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'G = ' + G.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Mean relative density, {{ species }} [Table A.11]</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'J_x = ' + Jx.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">{{ materialType === 'clt' ? 'CLT' : 'All other cases' }}</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'\\gamma_w = ' + yw.toFixed(2) + ' \\text{ N/mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Specified withdrawal resistance per mm</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'K_D = ' + KD.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">{{ loadDuration === 'short' ? 'Short term' : loadDuration === 'standard' ? 'Standard term' : 'Long term' }} [Table 5.1]</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'K_{SF} = ' + KSF.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">{{ moistureContent === 'dry' ? 'Dry' : 'Green' }} / {{ serviceCondition === 'dry' ? 'Dry' : 'Wet' }} service [Table 12.1]</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'K_T = ' + parseFloat(KT).toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">{{ parseFloat(KT) === 1 ? 'Untreated wood [Clause 5.3.4.3]' : 'Fire-retardant treatment [Clause 5.3.4.4, 12.2.1.7.3]' }}</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'Y_w = ' + Yw.toFixed(2) + '\\text{ N/mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Withdrawal resistance per mm</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'L_t = ' + Lt + '\\text{ mm}'" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Length of penetration</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'n_F = ' + nF" />
          </div>
          <div class="col-6">
            <p class="fst-italic">Number of fasteners</p>
          </div>
        </div>
        
        <div class="row">
          <div class="col-6">
            <KaTeXComponent :expression="'J_E = ' + JE.toFixed(2)" />
          </div>
          <div class="col-6">
            <p class="fst-italic">{{ endGrainLabel }}</p>
          </div>
        </div>
        
        <hr/>
        
        <div class="row" v-if="Prw">
          <div class="col-12">
            <h5>Result:</h5>
            <KaTeXComponent :expression="'P_{rw} = ' + Prw.toFixed(2) + '\\text{ N}'" />
          </div>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script>
import TopbarNav from '@/components/TopbarNav.vue'

export default {
  name: "LagScrewWithdrawal",
  components: { TopbarNav },
  data: function () {
    return {
      phi: 0.6,
      species: "D Fir-Larch",
      dF: 12.70,
      materialType: "standard",
      loadDuration: "standard",
      moistureContent: "dry",
      serviceCondition: "dry",
      KT: 1.0,
      Lt: 50,
      nF: 1,
      endGrainCondition: "other",
      densityTable: {
        "D Fir-Larch": 0.49,
        "Hem-Fir": 0.46,
        "Spruce-Pine-Fir": 0.42,
        "Northern Species": 0.35
      },
      loadDurationFactors: {
        "short": 1.15,
        "standard": 1.00,
        "long": 0.65
      },
      serviceConditionFactors: {
        "dry-dry": 1.00,
        "dry-wet": 0.67,
        "green-dry": 0.40,
        "green-wet": 0.40
      }
    };
  },
  computed: {
    G: function() {
      return this.densityTable[this.species];
    },
    Jx: function() {
      return this.materialType === "clt" ? 0.9 : 1.0;
    },
    KD: function() {
      return this.loadDurationFactors[this.loadDuration];
    },
    KSF: function() {
      const key = this.moistureContent + "-" + this.serviceCondition;
      return this.serviceConditionFactors[key];
    },
    yw: function() {
      return 59 * Math.pow(parseFloat(this.dF), 0.82) * Math.pow(this.G, 1.77) * this.Jx;
    },
    Yw: function() {
      return this.yw * this.KD * this.KSF * this.KT;
    },
    JE: function() {
      if (this.endGrainCondition === "endgrain") return 0.75;
      if (this.endGrainCondition === "clt_edge") return 0.67;
      return 1.00;
    },
    endGrainLabel: function() {
      if (this.endGrainCondition === "endgrain") return "End grain";
      if (this.endGrainCondition === "clt_edge") return "Panel edge of CLT";
      return "All other cases";
    },
    Prw: function() {
      return this.phi * this.Yw * this.Lt * this.nF * this.JE;
    }
  }
};
</script>

<style scoped>
</style>